import { SessionManager } from './session';
import { fetchWithTimeout, extractUtmParams, getReferrer, detectDevice } from './utils';

/**
 * Tipos de eventos para el tracking
 */
export enum EventType {
  PAGE_VIEW = 'page_view',
  PRODUCT_VIEW = 'product_view',
  ADD_TO_CART = 'add_to_cart',
  REMOVE_FROM_CART = 'remove_from_cart',
  UPDATE_CART = 'update_cart',
  CLEAR_CART = 'clear_cart',
  ADD_TO_WISHLIST = 'add_to_wishlist',
  REMOVE_FROM_WISHLIST = 'remove_from_wishlist',
  BEGIN_CHECKOUT = 'begin_checkout',
  PURCHASE = 'purchase',
  USER_REGISTER = 'user_register',
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  SEARCH = 'search',
  CUSTOM = 'custom'
}

/**
 * Interfaz para los datos de evento
 */
export interface EventData {
  [key: string]: any;
}

/**
 * Clase para manejar el tracking de eventos
 */
export class EventTracker {
  private apiUrl: string;
  private sessionManager: SessionManager;
  private queue: Array<{ eventType: EventType; eventData: EventData; funnelName?: string; funnelStep?: number }> = [];
  private processingQueue = false;
  
  constructor(apiUrl: string, sessionManager: SessionManager) {
    this.apiUrl = apiUrl;
    this.sessionManager = sessionManager;
    
    // Procesar la cola antes de que la página se descargue
    window.addEventListener('beforeunload', () => {
      this.flushQueueSync();
    });
  }
  
  /**
   * Rastrear un evento
   * @param eventType Tipo de evento
   * @param eventData Datos adicionales del evento
   * @param funnelName Nombre del embudo (opcional)
   * @param funnelStep Paso del embudo (opcional)
   */
  async track(
    eventType: EventType,
    eventData: EventData = {},
    funnelName?: string,
    funnelStep?: number
  ): Promise<void> {
    // Añadir a la cola
    this.queue.push({
      eventType,
      eventData,
      funnelName,
      funnelStep
    });
    
    // Procesar la cola si no se está procesando ya
    if (!this.processingQueue) {
      this.processQueue();
    }
  }
  
  /**
   * Envía un evento de forma inmediata sin añadirlo a la cola
   */
  async trackImmediate(
    eventType: EventType,
    eventData: EventData = {},
    funnelName?: string,
    funnelStep?: number
  ): Promise<Response | null> {
    try {
      // Enriquecer los datos del evento
      const enrichedData = this.enrichEventData(eventData);
      
      // Preparar los datos para enviar
      const payload = {
        eventType,
        eventData: enrichedData,
        funnelName,
        funnelStep
      };
      
      // Añadir el sessionId como header
      const headers = {
        'Content-Type': 'application/json',
        'X-Session-ID': this.sessionManager.getSessionId()
      };
      
      // Enviar al servidor
      const response = await fetchWithTimeout(`${this.apiUrl}/tracking/event`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include',
        timeout: 2000
      });
      
      if (!response.ok) {
        console.error(`Error tracking event: ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      console.error('Failed to track event:', error);
      return null;
    }
  }
  
  /**
   * Procesa la cola de eventos en segundo plano
   */
  private async processQueue(): Promise<void> {
    if (this.queue.length === 0 || this.processingQueue) {
      return;
    }
    
    this.processingQueue = true;
    
    try {
      // Tomar el primer evento de la cola
      const event = this.queue.shift();
      
      if (event) {
        await this.trackImmediate(
          event.eventType,
          event.eventData,
          event.funnelName,
          event.funnelStep
        );
      }
    } catch (error) {
      console.error('Error processing event queue:', error);
    } finally {
      this.processingQueue = false;
      
      // Si aún hay eventos en la cola, procesar el siguiente
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 100);
      }
    }
  }
  
  /**
   * Procesa todos los eventos pendientes de forma síncrona
   * (usado cuando la página se está descargando)
   */
  private flushQueueSync(): void {
    if (this.queue.length === 0 || !navigator.sendBeacon) {
      return;
    }
    
    try {
      // Crear un único payload con todos los eventos pendientes
      const events = [...this.queue];
      this.queue = [];
      
      // Enriquecer cada evento
      const enrichedEvents = events.map(event => ({
        ...event,
        eventData: this.enrichEventData(event.eventData)
      }));
      
      // Crear un blob para sendBeacon
      const blob = new Blob([JSON.stringify({ events: enrichedEvents })], {
        type: 'application/json'
      });
      
      // Enviar todos los eventos pendientes usando sendBeacon
      navigator.sendBeacon(`${this.apiUrl}/tracking/events-batch`, blob);
    } catch (error) {
      console.error('Error flushing event queue:', error);
    }
  }
  
  /**
   * Enriquece los datos del evento con información contextual
   */
  private enrichEventData(eventData: EventData): EventData {
    const deviceInfo = detectDevice();
    const utmParams = extractUtmParams();
    const referrer = getReferrer();
    
    return {
      ...eventData,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      referrer,
      ...utmParams,
      ...deviceInfo,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      sessionInfo: this.sessionManager.getSessionInfo()
    };
  }
} 